import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Store } from '@ngxs/store';
import { Logout, RefreshTokensStore, SetTokens } from '@stores/session/session.actions';
import { SessionState } from '@stores/session/session.state';
import { JwtServiceInterface, JwtTokens } from '@wizbii-utils/angular/jwt';
import { AuthenticationWebservice } from '@wizbii-utils/angular/webservices';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Injectable()
export class JwtService implements JwtServiceInterface {
  blacklistRoutes: (string | RegExp)[];

  constructor(
    private readonly store: Store,
    private readonly authService: AuthenticationWebservice,
    @Inject(PLATFORM_ID) platformId: any
  ) {
    const urlAuthUser = '((/v1/user).*(/email|password|overview|authentication))';
    const urlAuthAssociate = '((/associate).*(/apple|google|facebook))';

    const consentApiUrl = isPlatformBrowser(platformId) ? '/_api/consent-api' : 'http://consent-api';
    const authApiUrl = isPlatformBrowser(platformId) ? '/_api/auth-api' : 'http://auth-api';
    const accountApiUrl = isPlatformBrowser(platformId) ? '/_api/account-api' : 'http://account-api';
    const placeApiUrl = isPlatformBrowser(platformId) ? '/_api/place-api' : 'http://place-api';
    const jobApiUrl = isPlatformBrowser(platformId) ? '/_api/job-api' : 'http://job-api';
    const subscriptionApiUrl = isPlatformBrowser(platformId) ? '/_api/subscription-api' : 'http://subscription-api';
    const url = isPlatformBrowser(platformId) ? '' : 'http:/';

    this.blacklistRoutes = [
      'https://storage.googleapis.com',
      new RegExp(`${accountApiUrl}/v1/services/.+`, 'i'),
      `${accountApiUrl}/v1/login`,
      `${accountApiUrl}/v1/login/process`,
      `${accountApiUrl}/v1/logout`,
      `${accountApiUrl}/v1/logout/process`,
      new RegExp(`${subscriptionApiUrl}/v2/email-preferences$`, 'i'),
      `${subscriptionApiUrl}/v2/unsubscribe-reasons`,
      `${url}/account/assets/sprite.svg`,
      `${accountApiUrl}/v2/register-form/account`,
      `${accountApiUrl}/v1/login-by-google`,

      new RegExp(`${accountApiUrl}/v1/account$`, 'i'),
      new RegExp(`${accountApiUrl}/v1/country/.+`, 'i'),

      new RegExp(`${consentApiUrl}/v2/consent.+/account`, 'i'),
      new RegExp(`${consentApiUrl}/v2/consent/transform/.+`, 'i'),
      new RegExp(`${consentApiUrl}/v2/consent/transform/.+/.+`, 'i'),
      new RegExp(`${consentApiUrl}/v2/consent/.+/.+`, 'i'),
      new RegExp(`${accountApiUrl}/v2/register-form/.+`, 'i'),

      new RegExp(`${placeApiUrl}/v2/location.*`, 'i'),
      new RegExp(`${jobApiUrl}/api/jobs/.+`, 'i'),
      new RegExp(`${authApiUrl}(?!(${urlAuthUser}|${urlAuthAssociate}))`, 'i'),
    ];
  }

  getTokens(): Observable<JwtTokens> {
    return this.store.selectOnce(SessionState.tokens);
  }

  logout(): void {
    this.store.dispatch(new Logout());
  }

  refreshToken(tokens: JwtTokens): Observable<JwtTokens> {
    return this.authService
      .refreshToken(tokens)
      .pipe(
        switchMap((newTokens) => this.store.dispatch(new RefreshTokensStore(newTokens)).pipe(map(() => newTokens)))
      );
  }

  saveLocalTokens(jwtTokens: JwtTokens): void {
    this.store.dispatch(new SetTokens(jwtTokens));
  }
}
